import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { UserProfile } from "@/api/types";
import { mapSessionUserToProfile } from "@/features/auth/api/session";
import {
  getFirebaseAuth,
  getFirebaseIdToken,
  signOutFirebase,
  type FirebaseUser,
} from "@/features/auth/firebase/client";
import { authApiClient, sessionApi, sessionHandlers } from "@/features/auth/session";
import { clearAppQueryCache } from "@/app/QueryProvider";
import { ApiError } from "@/lib/errors";
import { getExtensionConnectContext, signOutConnectedExtension, watchExtensionAuthState } from "@/lib/extensionConnect";
import { onAuthStateChanged } from "firebase/auth";

interface AuthState {
  user: UserProfile | null;
  firebaseUser: FirebaseUser | null;
  status: "loading" | "authenticated" | "anonymous";
}

interface AuthContextValue extends AuthState {
  api: typeof authApiClient;
  establishSession: () => Promise<UserProfile>;
  updateUser: (user: UserProfile) => void;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const recoverInFlightRef = useRef<Promise<boolean> | null>(null);
  const userRef = useRef<UserProfile | null>(null);
  const firebaseUserRef = useRef<FirebaseUser | null>(null);
  const remoteSignOutRef = useRef(false);

  const [state, setState] = useState<AuthState>({
    user: null,
    firebaseUser: null,
    status: "loading",
  });

  const createSession = useCallback(async (): Promise<UserProfile> => {
    remoteSignOutRef.current = false;
    const idToken = await getFirebaseIdToken(true);
    if (!idToken) throw new Error("Sign in to continue.");
    const created = await sessionApi.createSession(idToken);
    const user = mapSessionUserToProfile(created.user);
    userRef.current = user;
    setState({
      user,
      firebaseUser: firebaseUserRef.current,
      status: "authenticated",
    });
    return user;
  }, []);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    if (recoverInFlightRef.current) return recoverInFlightRef.current;

    const run = async (): Promise<boolean> => {
      try {
        const user = await sessionApi.getMe();
        userRef.current = user;
        setState({
          user,
          firebaseUser: firebaseUserRef.current,
          status: "authenticated",
        });
        return true;
      } catch (error) {
        const firebaseUser = firebaseUserRef.current ?? getFirebaseAuth()?.currentUser;
        if (error instanceof ApiError && error.status === 401 && firebaseUser && !remoteSignOutRef.current) {
          firebaseUserRef.current = firebaseUser;
          try {
            await createSession();
            return true;
          } catch {
            // Fall through to anonymous.
          }
        }
        setState({
          user: null,
          firebaseUser: firebaseUserRef.current,
          status: "anonymous",
        });
        return false;
      }
    };

    recoverInFlightRef.current = run().finally(() => {
      recoverInFlightRef.current = null;
    });
    return recoverInFlightRef.current;
  }, [createSession]);

  const establishSession = useCallback(async (): Promise<UserProfile> => {
    if (await refreshSession() && userRef.current) return userRef.current;
    throw new Error("Sign in to continue.");
  }, [refreshSession]);

  useEffect(() => {
    userRef.current = state.user;
  }, [state.user]);

  useEffect(() => {
    sessionHandlers.setRecoverHandler(refreshSession);
  }, [refreshSession]);

  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth) {
      void refreshSession();
      return;
    }

    return onAuthStateChanged(auth, (next) => {
      firebaseUserRef.current = next;
      if (next) remoteSignOutRef.current = false;
      setState((current) => ({ ...current, firebaseUser: next }));
      void refreshSession();
    });
  }, [refreshSession]);

  const updateUser = useCallback((user: UserProfile) => {
    setState((current) => (current.user ? { ...current, user } : current));
  }, []);

  const signOut = useCallback(async () => {
    remoteSignOutRef.current = true;
    const pairingClientId = getExtensionConnectContext()?.clientId;
    await sessionApi.deleteSession(pairingClientId).catch(() => {
      // Local sign-out still proceeds when revoke is already complete.
    });
    await signOutConnectedExtension();
    await signOutFirebase();
    clearAppQueryCache();
    firebaseUserRef.current = null;
    setState({ user: null, firebaseUser: null, status: "anonymous" });
  }, []);

  useEffect(() => {
    let stopWatching: () => void = () => {};
    const connect = () => {
      stopWatching();
      stopWatching = watchExtensionAuthState((extensionState) => {
        const seenRevision = Number(localStorage.getItem("memoru_extension_auth_revision") || 0);
        if (extensionState.authRevision < seenRevision) return;
        localStorage.setItem("memoru_extension_auth_revision", String(extensionState.authRevision));
        if (extensionState.accountId && userRef.current && extensionState.accountId !== userRef.current.user_id) {
          remoteSignOutRef.current = true;
          void signOutFirebase().finally(() => {
            firebaseUserRef.current = null;
            clearAppQueryCache();
            setState({ user: null, firebaseUser: null, status: "anonymous" });
          });
          return;
        }
        if (!extensionState.pendingSignOut) return;
        remoteSignOutRef.current = true;
        void signOut();
      });
    };
    connect();
    window.addEventListener("memoru-extension-context", connect);
    return () => {
      window.removeEventListener("memoru-extension-context", connect);
      stopWatching();
    };
  }, [signOut]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      api: authApiClient,
      establishSession,
      updateUser,
      signOut,
      refreshSession,
    }),
    [state, establishSession, updateUser, signOut, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
